import {
    AfterViewInit, Component, OnInit,
} from '@angular/core';
import { FormInputComponent, SharedModule, StringCustomFieldConfig } from '@vendure/admin-ui/core';
import { FormControl } from '@angular/forms';
import { NotificationService } from '@vendure/admin-ui/core';
import { safeJSONParse } from '../../common/ui/utils';

export enum DistanceUnit {
    in = 'in',
    cm = 'cm',
}

export enum MassUnit {
    oz = 'oz',
    g = 'g',
}

export interface DimensionData {
    enabled: boolean;
    width:  number | null;
    height: number | null;
    length: number | null;
    distanceUnit: DistanceUnit;
    weight: number | null;
    massUnit: MassUnit;
}

@Component({
    selector: 'dimension-form-input',
    template: `
    <vdr-card>
        <!-- Row 1 – master toggle -->
        <vdr-form-field [label]="'Enabled'" class="mb-3">
            <clr-toggle-wrapper>
                <input clrToggle type="checkbox" [formControl]="enabledCtrl" />
                <label>{{ 'common.enabled' | translate }}</label>
            </clr-toggle-wrapper>
        </vdr-form-field>

        <!-- Dimension fields -->
        <div *ngIf="enabledCtrl.value" class="gap-x-8 gap-y-3">
            <div class="clr-row">
                <!-- Length -->
                <vdr-form-field [label]="'Length'" class="clr-col-3">
                    <input type="number" min="0" step="0.01" [formControl]="lengthCtrl" />
                </vdr-form-field>

                <!-- Width -->
                <vdr-form-field [label]="'Width'" class="clr-col-3">
                    <input type="number" min="0" step="0.01" [formControl]="widthCtrl" />
                </vdr-form-field>

                <!-- Height -->
                <vdr-form-field [label]="'Height'" class="clr-col-3">
                    <input type="number" min="0" step="0.01" [formControl]="heightCtrl" />
                </vdr-form-field>

                <!-- Distance unit -->
                <vdr-form-field [label]="'Distance Unit'" class="clr-col-3">
                    <select [formControl]="distanceUnitCtrl">
                        <option [ngValue]="DistanceUnit.in">Inches (in)</option>
                        <option [ngValue]="DistanceUnit.cm">Centimetres (cm)</option>
                    </select>
                </vdr-form-field>
            </div>

            <div class="clr-row">
                <!-- Weight -->
                <vdr-form-field [label]="'Weight'" class="clr-col-6">
                    <input type="number" min="0" step="0.01" [formControl]="weightCtrl" />
                </vdr-form-field>

                <!-- Mass unit -->
                <vdr-form-field [label]="'Mass Unit'" class="clr-col-6">
                    <select [formControl]="massUnitCtrl">
                        <option [ngValue]="MassUnit.oz">Ounces (oz)</option>
                        <option [ngValue]="MassUnit.g">Grams (g)</option>
                    </select>
                </vdr-form-field>
            </div>
        </div>
    </vdr-card>


    `,
    //styleUrls: ['../styles.scss'],
    standalone: true,
    imports: [SharedModule],
})
export class DimensionInputComponent
    implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit {

    /* ===== Vendure wires ===== */
    readonly: boolean;
    config: StringCustomFieldConfig;
    formControl: FormControl;

    /* ===== form controls ===== */
    enabledCtrl       = new FormControl();
    widthCtrl         = new FormControl();
    heightCtrl        = new FormControl();
    lengthCtrl        = new FormControl();
    distanceUnitCtrl  = new FormControl();
    weightCtrl        = new FormControl();
    massUnitCtrl      = new FormControl();

    /* ===== enums for template ===== */
    DistanceUnit = DistanceUnit;
    MassUnit     = MassUnit;

    private model: DimensionData;

    constructor(private notify: NotificationService) {}

    /* ---------- lifecycle ---------- */

    ngOnInit(): void {
        const defaultDimensionData: DimensionData = {
            enabled: false,
            width:  null,
            height: null,
            length: null,
            distanceUnit: DistanceUnit.cm,
            weight: null,
            massUnit: MassUnit.g,
        };
        this.model = safeJSONParse<DimensionData>(
            this.formControl.value,
            this.notify,
            defaultDimensionData,
        )!;

        /* prime controls */
        this.enabledCtrl.setValue(this.model.enabled);
        this.widthCtrl.setValue(this.model.width);
        this.heightCtrl.setValue(this.model.height);
        this.lengthCtrl.setValue(this.model.length);
        this.distanceUnitCtrl.setValue(this.model.distanceUnit);
        this.weightCtrl.setValue(this.model.weight);
        this.massUnitCtrl.setValue(this.model.massUnit);
    }

    ngAfterViewInit(): void {
        /* wire change streams */
        this.enabledCtrl.valueChanges.subscribe(v =>  {
            this.patch('enabled',v)
            if(!v) {
                this.patch('width', null);
                this.patch('height', null);
                this.patch('length', null);
                this.patch('distanceUnit', DistanceUnit.cm);
                this.patch('weight', null);
                this.patch('massUnit', MassUnit.g);
            }
        });
        this.widthCtrl.valueChanges.subscribe(v => this.patch('width',          v));
        this.heightCtrl.valueChanges.subscribe(v => this.patch('height',        v));
        this.lengthCtrl.valueChanges.subscribe(v => this.patch('length',        v));
        this.distanceUnitCtrl.valueChanges.subscribe(v => this.patch('distanceUnit', v));
        this.weightCtrl.valueChanges.subscribe(v => this.patch('weight',        v));
        this.massUnitCtrl.valueChanges.subscribe(v => this.patch('massUnit',    v));
    }

    /* ---------- helpers ---------- */

    private patch<K extends keyof DimensionData>(key: K, value: DimensionData[K]): void {
        if (this.model[key] === value) return;
        this.model[key] = value;
        this.serialize();
    }

    private serialize(): void {
        this.formControl.setValue(JSON.stringify(this.model));
        this.formControl.markAsDirty();
    }
}
